import { ProgramSchoolAssociation } from "../../../services/schools/school.types";

// SCRUM-816: helpers behind the "Permisos de contenido" section of the user
// form. The default permissions mirror what a teacher effectively sees today
// (SCRUM-759/802): the school's catalog trimmed to the section — Kinder or
// Primaria — of their main grade(s).

export type GradeSection = "kinder" | "primaria";

export interface PermissionOption {
  value: number;
  label: string;
}

export interface DefaultPermissions {
  grades: number[];
  subjects: number[];
}

// Grade codes are K1-K3 for Kinder and P1-P6 for Primaria. A missing code
// falls into primaria so an unknown grade is never hidden by accident.
export const sectionOfGradeCode = (
  code: string | null | undefined
): GradeSection => (code?.toUpperCase().startsWith("K") ? "kinder" : "primaria");

// Unique grades/subjects across the school's licensed programs, in catalog
// order. Programs from an older backend (no grade/subject) are skipped.
export const derivePermissionOptions = (
  programs: ProgramSchoolAssociation[]
): { gradeOptions: PermissionOption[]; subjectOptions: PermissionOption[] } => {
  const gradeOptions = new Map<number, string>();
  const subjectOptions = new Map<number, string>();

  for (const program of programs) {
    if (program.grade && !gradeOptions.has(program.grade.id)) {
      gradeOptions.set(program.grade.id, program.grade.code ?? program.grade.grade);
    }
    if (program.subject && !subjectOptions.has(program.subject.id)) {
      subjectOptions.set(program.subject.id, program.subject.name);
    }
  }

  const toOptions = (entries: Map<number, string>): PermissionOption[] =>
    Array.from(entries, ([value, label]) => ({ value, label }));

  return {
    gradeOptions: toOptions(gradeOptions),
    subjectOptions: toOptions(subjectOptions),
  };
};

// Default preselection: every school grade in the section(s) of the selected
// main grade(s), plus the subjects of the programs in those grades. With no
// main grade yet, the whole catalog is preselected — failing towards showing
// MORE, never towards hiding content a teacher needs.
export const computeDefaultPermissions = (
  programs: ProgramSchoolAssociation[],
  mainGradeIds: number[]
): DefaultPermissions => {
  const sections = new Set<GradeSection>();
  for (const program of programs) {
    if (program.grade && mainGradeIds.includes(program.grade.id)) {
      sections.add(sectionOfGradeCode(program.grade.code));
    }
  }

  const inScope = (program: ProgramSchoolAssociation): boolean =>
    sections.size === 0 ||
    sections.has(sectionOfGradeCode(program.grade?.code));

  const grades = new Set<number>();
  const subjects = new Set<number>();
  for (const program of programs) {
    if (!program.grade || !program.subject || !inScope(program)) continue;
    grades.add(program.grade.id);
    subjects.add(program.subject.id);
  }

  return { grades: Array.from(grades), subjects: Array.from(subjects) };
};
